import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { saveAiInsight, getAiInsight } from '../../firebase/db';

const styles = {
    toggle: {
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        gap: '12px', marginBottom: '16px', cursor: 'pointer', userSelect: 'none',
    },
    label: {
        fontWeight: 'bold', fontSize: '14px', color: '#4b2e83',
    },
    indicator: {
        fontSize: '13px', color: '#666', fontStyle: 'italic',
    },
    panel: {
        backgroundColor: '#f5f3fa', border: '1px solid #e0d8f0', borderRadius: '8px',
        padding: '20px', marginBottom: '20px',
    },
    content: {
        fontSize: '14px', lineHeight: '1.6', color: '#333',
    },
    generateBtn: {
        display: 'block', margin: '0 auto', padding: '12px 28px',
        backgroundColor: '#4b2e83', color: '#fff', border: 'none',
        borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
    },
    regenerateBtn: {
        display: 'block', marginLeft: 'auto', marginTop: '16px', padding: '8px 20px',
        backgroundColor: '#fff', color: '#4b2e83', border: '2px solid #4b2e83',
        borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px',
    },
    loading: {
        textAlign: 'center', padding: '30px', color: '#4b2e83', fontSize: '14px',
    },
    error: {
        textAlign: 'center', padding: '20px', color: '#d32f2f', fontSize: '14px',
    },
    retryBtn: {
        display: 'block', margin: '10px auto 0', padding: '8px 20px',
        backgroundColor: '#d32f2f', color: '#fff', border: 'none',
        borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px',
    },
    staleNotice: {
        fontSize: '12px', color: '#e65100', fontStyle: 'italic',
        marginBottom: '12px', textAlign: 'center',
    },
};

const AiInsightsPanel = ({ gameId, cacheDocId, filterHash, buildSummary, insightType, teamName }) => {
    const [open, setOpen] = useState(false);
    const [content, setContent] = useState(null);
    const [cachedFilterHash, setCachedFilterHash] = useState(null);
    const [generatedAt, setGeneratedAt] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [cacheChecked, setCacheChecked] = useState(false);

    const isStale = content && cachedFilterHash && cachedFilterHash !== filterHash;

    // Load cached insight on mount
    useEffect(() => {
        if (!gameId || !cacheDocId) return;
        const loadCache = async () => {
            try {
                const cached = await getAiInsight(gameId, cacheDocId);
                if (cached) {
                    setContent(cached.content);
                    setCachedFilterHash(cached.filterHash);
                    setGeneratedAt(cached.generatedAt);
                    setOpen(true);
                }
            } catch (err) {
                console.error('Error loading cached insight:', err);
            } finally {
                setCacheChecked(true);
            }
        };
        loadCache();
    }, [gameId, cacheDocId]);

    const generate = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const functions = getFunctions();
            const generateInsightsFn = httpsCallable(functions, 'generate_ai_insights');
            const summary = buildSummary();

            const result = await generateInsightsFn({
                type: insightType,
                gameSummary: summary,
                teamName: teamName || null,
            });

            const newContent = result.data.content;
            setContent(newContent);
            setCachedFilterHash(filterHash);
            setGeneratedAt(new Date().toISOString());
            setOpen(true);

            // Save to Firestore cache
            await saveAiInsight(gameId, cacheDocId, newContent, filterHash);
        } catch (err) {
            console.error('Error generating insights:', err);
            const code = err.code || '';
            const detail = err.message || '';
            let userMessage;
            if (code === 'functions/unavailable' || detail.includes('Connection error') || detail.includes('Could not reach')) {
                userMessage = 'Could not connect to the AI service. This may be a temporary issue — please try again in a moment.';
            } else if (code === 'functions/internal') {
                userMessage = `AI service error: ${detail}`;
            } else if (code === 'functions/unauthenticated') {
                userMessage = 'Authentication required. Please sign in and try again.';
            } else {
                userMessage = detail || 'Failed to generate AI insights. Please try again.';
            }
            setError(userMessage);
        } finally {
            setLoading(false);
        }
    }, [gameId, cacheDocId, filterHash, buildSummary, insightType, teamName]);

    const timeAgo = (isoString) => {
        if (!isoString) return '';
        const diff = Date.now() - new Date(isoString).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins} min ago`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours}h ago`;
        return `${Math.floor(hours / 24)}d ago`;
    };

    if (!cacheChecked) return null;

    return (
        <div>
            <div style={styles.toggle} onClick={() => setOpen(!open)}>
                <span style={styles.label}>
                    AI Insights {open ? '\u25B2' : '\u25BC'}
                </span>
                <span style={styles.indicator}>
                    {content
                        ? `Generated ${timeAgo(generatedAt)}`
                        : 'Click to generate AI analysis'}
                </span>
            </div>

            {open && (
                <div style={styles.panel}>
                    {loading && (
                        <div style={styles.loading}>
                            <div style={{ fontSize: '24px', marginBottom: '8px' }}>
                                {'\u2728'}
                            </div>
                            Generating AI analysis... This may take 15-30 seconds.
                        </div>
                    )}

                    {error && !loading && (
                        <div>
                            <div style={styles.error}>{error}</div>
                            <button style={styles.retryBtn} onClick={generate}>
                                Try Again
                            </button>
                        </div>
                    )}

                    {!content && !loading && !error && (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <p style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
                                Generate an AI-powered analysis of the game performance,
                                including strategic insights and "what if" scenarios.
                            </p>
                            <button style={styles.generateBtn} onClick={generate}>
                                Generate AI Insights
                            </button>
                        </div>
                    )}

                    {content && !loading && (
                        <div>
                            {isStale && (
                                <div style={styles.staleNotice}>
                                    Filters have changed since this analysis was generated. Click "Regenerate" for updated insights.
                                </div>
                            )}
                            <div style={styles.content}>
                                <ReactMarkdown>{content}</ReactMarkdown>
                            </div>
                            <button style={styles.regenerateBtn} onClick={generate}>
                                Regenerate
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AiInsightsPanel;
